import React, { useRef } from "react";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { loadGMaps, newMap } from "tsi-common-react/src/utils/maps";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconMarker2 from "../../assets/img/retail-locator/icon-marker2.png";

type IProps = {
    lat: number;
    lng: number;
    staticMapUrl: string;
};

// Await Google Maps before instantiating: the full-document SSR shell has no
// synchronous pre-bundle Maps script, so `google.maps` can't be touched at
// module scope.
async function initStoreMap(
    mapCanvas: HTMLElement,
    lat: number,
    lng: number,
): Promise<void> {
    try {
        await loadGMaps();
        const latlng = new google.maps.LatLng(lat, lng);
        const map = newMap(mapCanvas, {
            center: latlng,
            zoom: 16,
            scrollwheel: false,
            draggable: true,
            disableDefaultUI: false,
            clickableIcons: false,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
        });
        const marker = new google.maps.Marker({
            position: latlng,
            title: t`Tempur-Pedic Store`,
            icon: iconMarker2,
        });
        marker.setMap(map);
        map.set("styles", [
            {
                featureType: "landscape",
                elementType: "geometry",
                stylers: [
                    { hue: "#ffff00" },
                    { saturation: 30 },
                    { lightness: 10 },
                ],
            },
        ]);
        // Handle title tag - a11y approved
        google.maps.event.addListenerOnce(map, "idle", () => {
            const mapDoc = getDocument();
            const iframe =
                mapDoc?.querySelector<HTMLIFrameElement>("#map_canvas iframe");
            if (iframe) {
                iframe.title = t`Google Maps`;
            }
        });
    } catch (e) {
        console.warn("Google Maps failed to initialize", e);
    }
}

/**
 * The store-detail hero: the interactive Google Map plus the print-only static
 * map. `#map_canvas` keeps its id/class for CSS parity but carries NO
 * `data-lat`/`data-lng`, so the global `RetailLocator/index.tsx` IIFE (which
 * runs at client init, before this component hydrates) finds nothing to bind --
 * this component owns map init via the ref + `useMountEffect` instead.
 */
export const StoreDetailMap = (props: IProps): React.JSX.Element => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const hasCoords = Boolean(props.lat && props.lng);
    useMountEffect(() => {
        if (hasCoords && mapRef.current) {
            void initStoreMap(mapRef.current, props.lat, props.lng);
        }
    });
    return (
        <div className="hero l-full-width">
            <div id="map_canvas" className="hero__map" ref={mapRef} />
            <div id="map_canvas-print" className="hero__map">
                {/* Load in CORS mode so the browser always sends `Origin`:
                    Google falls back to it when `Referer` is stripped, and
                    website-restricted keys need one of them from Oct 2027. */}
                <img
                    alt="Map"
                    crossOrigin="anonymous"
                    src={props.staticMapUrl}
                />
            </div>
        </div>
    );
};
